import React, { useState, useContext, useEffect } from 'react';
import { fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Camera, AlertCircle, CheckCircle2 } from 'lucide-react';

const TemplateBasedInspectionForm = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [serviceLocations, setServiceLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [buildingAreas, setBuildingAreas] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    service_location_id: '',
    template_id: '',
    inspector_name: user?.name || '',
    inspection_date: new Date().toISOString().split('T')[0]
  });

  const [sectionData, setSectionData] = useState({});
  const [selectedRooms, setSelectedRooms] = useState({});
  const [signature, setSignature] = useState('');
  const [showDebug, setShowDebug] = useState(true); // Debug panel visibility
  const [showLocationList, setShowLocationList] = useState(false); // Show raw location list
  const [useNativeDropdown, setUseNativeDropdown] = useState(false); // Native select fallback

  useEffect(() => {
    fetchCustomers();
    fetchServiceLocations();
    fetchTemplates();
  }, []);

  useEffect(() => {
    // Filter locations by selected customer
    if (formData.customer_id && serviceLocations.length > 0) {
      const filtered = serviceLocations.filter(loc => 
        loc.customer_name === formData.customer_id
      );
      setFilteredLocations(filtered);
      console.log(`✓ Filtered ${filtered.length} locations for customer: "${formData.customer_id}"`);
      if (filtered.length > 0) {
        console.log('Available locations:', filtered.map(l => l.location_name));
      }
    } else {
      setFilteredLocations([]);
      if (!formData.customer_id) {
        console.log('No customer selected');
      } else if (serviceLocations.length === 0) {
        console.log('⚠ Service locations not loaded yet');
      }
    }
  }, [formData.customer_id, serviceLocations]);

  useEffect(() => {
    if (formData.service_location_id) {
      fetchBuildingAreas(formData.service_location_id);
    }
  }, [formData.service_location_id]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/service-locations/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(response.data || []);
      console.log('Fetched customers:', response.data?.length);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
      setCustomers([]);
    }
  };

  const fetchServiceLocations = async () => {
    try {
      const response = await axios.get(`${API}/service-locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setServiceLocations(response.data || []);
      console.log('Fetched service locations:', response.data?.length);
    } catch (error) {
      console.error('Failed to fetch service locations:', error);
      toast.error('Failed to load service locations');
      setServiceLocations([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/inspection-templates/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data || []);
      console.log('Fetched templates:', response.data?.length);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load templates');
      setTemplates([]);
    }
  };

  const fetchBuildingAreas = async (locationId) => {
    try {
      const response = await axios.get(`${API}/building-areas/location/${locationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBuildingAreas(response.data || []);
      console.log('Fetched building areas:', response.data?.length);
    } catch (error) {
      console.error('Failed to fetch building areas:', error);
      setBuildingAreas([]);
    }
  };

  const loadTemplate = async (templateId) => {
    try {
      const response = await axios.get(`${API}/inspection-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedTemplate(response.data);
      
      // Initialize section data
      const initialData = {};
      response.data.sections.forEach(section => {
        initialData[section.section_id] = {
          ratings: {},
          notes: {},
          photos: {}
        };
        section.inspection_points.forEach((point, idx) => {
          initialData[section.section_id].ratings[idx] = 5;
          initialData[section.section_id].notes[idx] = '';
          initialData[section.section_id].photos[idx] = [];
        });
      });
      setSectionData(initialData);
      setSelectedRooms({});
    } catch (error) {
      console.error('Failed to load template:', error);
      toast.error('Failed to load inspection template');
    }
  };

  const handleTemplateChange = (templateId) => {
    setFormData({ ...formData, template_id: templateId });
    loadTemplate(templateId);
  };

  const handleRatingChange = (sectionId, pointIndex, rating) => {
    setSectionData(prev => ({
      ...prev,
      [sectionId]: {
        ...prev[sectionId],
        ratings: {
          ...prev[sectionId].ratings,
          [pointIndex]: parseInt(rating)
        }
      }
    }));
  };

  const handlePhotoUpload = async (sectionId, pointIndex, files) => {
    const formDataUpload = new FormData();
    Array.from(files).forEach(file => {
      formDataUpload.append('file', file);
    });

    try {
      const response = await axios.post(`${API}/photos/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const newPhotos = response.data.filenames || [response.data.filename];
      
      setSectionData(prev => ({
        ...prev,
        [sectionId]: {
          ...prev[sectionId],
          photos: {
            ...prev[sectionId].photos,
            [pointIndex]: [...(prev[sectionId].photos[pointIndex] || []), ...newPhotos]
          }
        }
      }));

      toast.success('Photos uploaded successfully');
    } catch (error) {
      console.error('Photo upload failed:', error);
      toast.error('Failed to upload photos');
    }
  };

  const handleRoomSelection = (sectionId, roomId) => {
    setSelectedRooms(prev => {
      const current = prev[sectionId] || [];
      const isSelected = current.includes(roomId);
      
      if (isSelected) {
        return { ...prev, [sectionId]: current.filter(id => id !== roomId) };
      } else {
        return { ...prev, [sectionId]: [...current, roomId] };
      }
    });
  };

  const validateForm = () => {
    if (!formData.service_location_id || !formData.template_id) {
      toast.error('Please select both location and template');
      return false;
    }

    if (!signature || signature.trim().length === 0) {
      toast.error('Inspector signature is required to submit inspection');
      return false;
    }

    // Check minimum room/area selections
    for (const section of selectedTemplate.sections) {
      if (section.min_selections) {
        const selected = selectedRooms[section.section_id] || [];
        if (selected.length < section.min_selections) {
          toast.error(`${section.title_en} requires at least ${section.min_selections} selections`);
          return false;
        }
      }
    }

    // Check for mandatory POA on low ratings
    let hasLowRatings = false;
    Object.values(sectionData).forEach(section => {
      Object.values(section.ratings).forEach(rating => {
        if (rating <= 2) hasLowRatings = true;
      });
    });

    if (hasLowRatings) {
      toast.warning('Low ratings detected. Please ensure POA (Plan of Action) is documented in notes.');
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);

    try {
      const selectedLocation = serviceLocations.find(loc => loc.id === formData.service_location_id);
      
      // Calculate overall rating
      let totalRating = 0;
      let ratingCount = 0;
      Object.values(sectionData).forEach(section => {
        Object.values(section.ratings).forEach(rating => {
          totalRating += rating;
          ratingCount++;
        });
      });
      const overallRating = ratingCount > 0 ? (totalRating / ratingCount).toFixed(2) : 5;

      // Construct inspection points
      const inspectionPoints = [];
      selectedTemplate.sections.forEach(section => {
        section.inspection_points.forEach((point, idx) => {
          const rating = sectionData[section.section_id].ratings[idx];
          const notes = sectionData[section.section_id].notes[idx];
          const photos = sectionData[section.section_id].photos[idx] || [];

          inspectionPoints.push({
            section_id: section.section_id,
            section_title_en: section.title_en,
            section_title_es: section.title_es,
            point_name_en: point.point_en,
            point_name_es: point.point_es,
            rating: rating,
            notes: notes,
            photos: photos,
            requires_poa: rating <= 2
          });
        });
      });

      const payload = {
        customer_id: selectedLocation?.customer_id || '',
        service_location_id: formData.service_location_id,
        service_type: selectedTemplate.template_type,
        inspector_name: formData.inspector_name,
        inspection_date: formData.inspection_date,
        cleanliness_rating: Math.round(parseFloat(overallRating)),
        overall_rating: parseFloat(overallRating),
        inspection_points: inspectionPoints,
        area_type: selectedTemplate.template_name,
        notes: JSON.stringify({ 
          template_id: formData.template_id,
          selected_rooms: selectedRooms 
        }),
        signature: signature,
        poa_items: []
      };

      console.log('Submitting inspection payload:', payload);

      const response = await axios.post(`${API}/cleaning-inspections`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Inspection submitted successfully:', response.data);
      toast.success(`✅ Inspection ${response.data.inspection_number} submitted successfully!`);
      
      // Navigate to inspection list after short delay to show success message
      setTimeout(() => {
        navigate('/field/inspections/list');
      }, 1500);
    } catch (error) {
      console.error('Submission failed:', error);
      toast.error(error.response?.data?.detail?.message || 'Failed to submit inspection');
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    if (rating <= 2) return 'text-red-600';
    if (rating === 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (!selectedTemplate) {
    const buildInfo = {
      version: 'v2.0.1-synovus-fix',
      timestamp: '2025-12-04T16:30:00Z',
      environment: 'preview'
    };
    
    return (
      <div className="p-6 max-w-2xl mx-auto">
        {/* BUILD STAMP */}
        <div className="mb-4 flex justify-end">
          <div className="text-xs bg-slate-800 text-white px-3 py-2 rounded shadow-lg font-mono">
            <div className="flex items-center gap-4">
              <span>🏗️ Build: {buildInfo.version}</span>
              <span>📅 {_fmtDateTime(buildInfo.timestamp)}</span>
              <span className="px-2 py-0.5 bg-blue-600 rounded">
                {buildInfo.environment.toUpperCase()}
              </span>
            </div>
          </div>
        </div>
        
        {/* DEBUG PANEL */}
        {showDebug && (
          <Card className="mb-4 border-2 border-yellow-500 bg-yellow-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between">
                <span>🐛 Debug Panel</span>
                <button 
                  onClick={() => setShowDebug(false)}
                  className="text-xs bg-yellow-200 px-2 py-1 rounded hover:bg-yellow-300"
                >
                  Hide
                </button>
              </CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <strong>Selected Customer:</strong>
                  <div className="font-mono bg-white p-1 rounded mt-1">
                    {formData.customer_id || '(none)'}
                  </div>
                </div>
                <div>
                  <strong>Selected Location ID:</strong>
                  <div className="font-mono bg-white p-1 rounded mt-1 text-xs overflow-hidden">
                    {formData.service_location_id || '(none)'}
                  </div>
                </div>
                <div>
                  <strong>Total Customers Loaded:</strong>
                  <div className="font-mono bg-white p-1 rounded mt-1">
                    {customers.length}
                  </div>
                </div>
                <div>
                  <strong>Total Locations Loaded:</strong>
                  <div className="font-mono bg-white p-1 rounded mt-1">
                    {serviceLocations.length}
                  </div>
                </div>
                <div>
                  <strong>Filtered Locations Count:</strong>
                  <div className="font-mono bg-white p-1 rounded mt-1">
                    {filteredLocations.length}
                  </div>
                </div>
                <div>
                  <strong>Filter Field Used:</strong>
                  <div className="font-mono bg-white p-1 rounded mt-1">
                    customer_name
                  </div>
                </div>
              </div>
              {filteredLocations.length > 0 && (
                <div className="mt-2">
                  <strong>First 3 Filtered Locations:</strong>
                  <div className="bg-white p-2 rounded mt-1 space-y-1">
                    {filteredLocations.slice(0, 3).map((loc, idx) => (
                      <div key={loc.id} className="text-xs">
                        {idx + 1}. {loc.location_name} (id: {loc.id.substring(0, 8)}...)
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {formData.customer_id && filteredLocations.length === 0 && serviceLocations.length > 0 && (
                <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded">
                  <strong className="text-red-700">⚠️ FILTERING ISSUE:</strong>
                  <div className="mt-1">
                    Customer "{formData.customer_id}" selected but 0 locations match.
                  </div>
                  <div className="mt-1">
                    Check: Are customer names in dropdown exactly matching customer_name field in locations?
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        <Card>
          <CardHeader>
            <CardTitle>Template-Based Inspection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>1. Select Customer</Label>
              <Select 
                value={formData.customer_id} 
                onValueChange={(val) => {
                  console.log('Customer selected:', val);
                  setFormData({ 
                    ...formData, 
                    customer_id: val,
                    service_location_id: '', // Reset location when customer changes
                    template_id: '' // Reset template when customer changes
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(customerName => (
                    <SelectItem key={customerName} value={customerName}>
                      {customerName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* LIVE DATA PREVIEW CONTROLS */}
            <div className="mb-4 p-4 bg-slate-100 rounded border-2 border-slate-300">
              <div className="text-sm font-semibold mb-2">🔧 Testing Controls</div>
              <div className="flex gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => {
                    fetchServiceLocations();
                    toast.success(`Reloaded locations. Total: ${serviceLocations.length}`);
                  }}
                  className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                >
                  🔄 Reload Locations
                </button>
                <button
                  type="button"
                  onClick={() => setShowLocationList(!showLocationList)}
                  className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                >
                  {showLocationList ? '❌' : '📋'} {showLocationList ? 'Hide' : 'Show'} First 10 Locations
                </button>
                <label className="flex items-center gap-2 px-3 py-1.5 bg-purple-600 text-white text-xs rounded cursor-pointer hover:bg-purple-700">
                  <input
                    type="checkbox"
                    checked={useNativeDropdown}
                    onChange={(e) => setUseNativeDropdown(e.target.checked)}
                    className="w-4 h-4"
                  />
                  Use Native Dropdown
                </label>
              </div>
              {showLocationList && (
                <div className="mt-2 p-3 bg-white rounded border border-slate-300 max-h-64 overflow-y-auto">
                  <div className="text-xs font-semibold mb-2">
                    Filtered Locations ({filteredLocations.length} total):
                  </div>
                  {filteredLocations.length === 0 ? (
                    <div className="text-xs text-red-600">No locations found for selected customer</div>
                  ) : (
                    <div className="space-y-1 text-xs font-mono">
                      {filteredLocations.slice(0, 10).map((loc, idx) => (
                        <div key={loc.id} className="border-b border-slate-200 pb-1">
                          {idx + 1}. {loc.location_name} — {loc.id}
                        </div>
                      ))}
                      {filteredLocations.length > 10 && (
                        <div className="text-slate-500 pt-1">
                          ... and {filteredLocations.length - 10} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <Label>2. Select Service Location</Label>
              {useNativeDropdown ? (
                // NATIVE HTML SELECT FALLBACK
                <select
                  value={formData.service_location_id}
                  onChange={(e) => {
                    const val = e.target.value;
                    console.log('Location selected (native):', val);
                    const selectedLoc = filteredLocations.find(l => l.id === val);
                    console.log('Location details:', selectedLoc?.location_name);
                    setFormData({ ...formData, service_location_id: val });
                  }}
                  disabled={!formData.customer_id}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">
                    {!formData.customer_id 
                      ? "First select a customer" 
                      : filteredLocations.length === 0 
                        ? "No locations available"
                        : `Select location (${filteredLocations.length} available)`}
                  </option>
                  {filteredLocations.map(loc => (
                    <option key={loc.id} value={loc.id}>
                      {loc.location_name}
                    </option>
                  ))}
                </select>
              ) : (
                // CUSTOM SELECT COMPONENT
                <Select 
                value={formData.service_location_id} 
                onValueChange={(val) => {
                  console.log('Location selected:', val);
                  const selectedLoc = filteredLocations.find(l => l.id === val);
                  console.log('Location details:', selectedLoc?.location_name);
                  setFormData({ ...formData, service_location_id: val });
                }}
                disabled={!formData.customer_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !formData.customer_id 
                      ? "First select a customer" 
                      : filteredLocations.length === 0 
                        ? "No locations available for this customer"
                        : `Select location (${filteredLocations.length} available)`
                  } />
                </SelectTrigger>
                <SelectContent>
                  {filteredLocations.map(loc => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.location_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              )}
              {/* Error messages (shown for both native and custom) */}
              {formData.customer_id && filteredLocations.length === 0 && serviceLocations.length > 0 && (
                <p className="text-sm text-red-600 mt-1">
                  ⚠ No service locations found for "{formData.customer_id}"
                </p>
              )}
              {formData.customer_id && serviceLocations.length === 0 && (
                <p className="text-sm text-amber-600 mt-1">
                  ⏳ Loading service locations...
                </p>
              )}
            </div>

            <div>
              <Label>3. Select Inspection Template</Label>
              <Select 
                value={formData.template_id} 
                onValueChange={handleTemplateChange}
                disabled={!formData.service_location_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={
                    !formData.service_location_id 
                      ? "First select a location" 
                      : "Select template"
                  } />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(template => (
                    <SelectItem key={template.template_id} value={template.template_id}>
                      {template.template_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="p-4 bg-blue-50 rounded border border-blue-200">
              <p className="text-sm text-blue-800">
                📋 Follow the steps above to begin the inspection:
              </p>
              <ul className="text-sm text-blue-700 mt-2 ml-4 list-disc">
                <li>Select customer {formData.customer_id && '✅'}</li>
                <li>Select service location {formData.service_location_id && '✅'}</li>
                <li>Select inspection template {formData.template_id && '✅'}</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>{selectedTemplate.template_name}</span>
              <span className="text-sm font-normal text-gray-500">
                {selectedTemplate.template_type}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedTemplate.special_instructions && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  <AlertCircle className="inline w-4 h-4 mr-1" />
                  {selectedTemplate.special_instructions}
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label>Inspector Name</Label>
                <input
                  type="text"
                  value={formData.inspector_name}
                  onChange={(e) => setFormData({ ...formData, inspector_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
              <div>
                <Label>Inspection Date</Label>
                <input
                  type="date"
                  value={formData.inspection_date}
                  onChange={(e) => setFormData({ ...formData, inspection_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {selectedTemplate.sections.map((section, sectionIdx) => (
          <Card key={section.section_id}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>{section.title_en}</span>
                {section.photo_required && (
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    Photo Required
                  </span>
                )}
              </CardTitle>
              {section.min_selections && (
                <p className="text-sm text-gray-600">
                  Select at least {section.min_selections} {section.min_selections > 1 ? 'areas' : 'area'}
                </p>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {section.min_selections && buildingAreas.length > 0 && (
                <div className="mb-4 p-4 bg-gray-50 rounded">
                  <Label className="mb-2 block">Select Areas/Rooms:</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {buildingAreas.map(area => (
                      <label key={area.id} className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(selectedRooms[section.section_id] || []).includes(area.id)}
                          onChange={() => handleRoomSelection(section.section_id, area.id)}
                          className="rounded"
                        />
                        <span className="text-sm">{area.area_name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Selected: {(selectedRooms[section.section_id] || []).length} / {section.min_selections} required
                  </p>
                </div>
              )}

              {section.inspection_points.map((point, pointIdx) => (
                <div key={pointIdx} className="p-4 border rounded space-y-3">
                  <p className="font-medium text-sm">{point.point_en}</p>
                  
                  <div className="flex items-center gap-4">
                    <Label className="text-sm">Rating:</Label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(rating => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => handleRatingChange(section.section_id, pointIdx, rating)}
                          className={`w-10 h-10 rounded border-2 font-bold transition ${
                            sectionData[section.section_id].ratings[pointIdx] === rating
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white border-gray-300 hover:border-blue-400'
                          }`}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                    <span className={`text-sm font-medium ${getRatingColor(sectionData[section.section_id].ratings[pointIdx])}`}>
                      {sectionData[section.section_id].ratings[pointIdx] <= 2 ? 'Needs Attention' : 
                       sectionData[section.section_id].ratings[pointIdx] === 3 ? 'Fair' : 'Good'}
                    </span>
                  </div>

                  <div>
                    <Label className="text-sm">Notes (optional):</Label>
                    <textarea
                      value={sectionData[section.section_id].notes[pointIdx]}
                      onChange={(e) => setSectionData(prev => ({
                        ...prev,
                        [section.section_id]: {
                          ...prev[section.section_id],
                          notes: {
                            ...prev[section.section_id].notes,
                            [pointIdx]: e.target.value
                          }
                        }
                      }))}
                      className="w-full px-3 py-2 border rounded text-sm"
                      rows="2"
                      placeholder="Add notes, observations, or corrective actions..."
                    />
                  </div>

                  <div>
                    <Label className="text-sm flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      Photos:
                    </Label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handlePhotoUpload(section.section_id, pointIdx, e.target.files)}
                      className="text-sm"
                    />
                    {sectionData[section.section_id].photos[pointIdx]?.length > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        <CheckCircle2 className="inline w-3 h-3" /> {sectionData[section.section_id].photos[pointIdx].length} photo(s) uploaded
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Inspector Signature</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label>Full Name (acts as digital signature) *</Label>
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Type your full name to sign this inspection"
                required
                className="w-full px-3 py-2 border rounded"
              />
              <p className="text-xs text-gray-500">
                By typing your name, you certify that this inspection was conducted thoroughly and accurately.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/field/inspections/list')}>
            Cancel
          </Button>
          <Button type="submit" disabled={loading || !signature} className="flex-1">
            {loading ? 'Submitting...' : 'Submit Inspection'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TemplateBasedInspectionForm;
